import type { OpenTask } from "@storyboard/dispatch-view/task";
import type { DispatchGrain } from "@storyboard/dispatch-view/view";
import { type DispatchTableRow, rowFeedId, rowUnacknowledged } from "./dispatchTableRow";
import { rowTaskMenuActions, type TaskMenuAction, taskMenuLabel } from "./taskMenu";
import type { TaskFold } from "./tasks";

/** An acknowledgement entry: handle what was right-clicked, or take its handling back. */
export type AcknowledgementMenuAction = { kind: "acknowledge" } | { kind: "unacknowledge" };

/** Anything a Dispatch right-click menu runs: an acknowledgement entry or a task entry. */
export type DispatchMenuAction = AcknowledgementMenuAction | TaskMenuAction;

/** One entry of a Dispatch right-click menu: what it runs, what it reads, and the key that does the same. */
export type DispatchMenuEntry = {
  action: DispatchMenuAction;
  label: string;
  /** The key that does the same to what was right-clicked, as its keycaps read ("A", "Shift+A"); null for none. */
  hint: string | null;
};

/** What an acknowledgement entry names: nothing for a row, message, or event, else "task" or "channel". */
export type AcknowledgementNoun = "item" | "task" | "channel";

/** The keys a menu's entries hint at, as their keycaps read; null for an action no key fires. */
export type DispatchMenuHints = { acknowledge: string | null; unacknowledge: string | null; task: string | null };

/** What the acknowledgement entry offers: handling what was right-clicked, or taking it back once handled. */
export type MenuAcknowledgement = { noun: AcknowledgementNoun; acknowledged: boolean };

/** The label an acknowledgement entry reads: "Acknowledge", or "Unacknowledge task", and so on. */
function acknowledgementLabel({ noun, acknowledged }: MenuAcknowledgement): string {
  const verb = acknowledged ? "Unacknowledge" : "Acknowledge";
  return noun === "item" ? verb : `${verb} ${noun}`;
}

/**
 * The entries of every Dispatch right-click menu, in the one order the table and History share: the acknowledgement
 * entry, then the task entries.
 *
 * The acknowledgement entry reads "Acknowledge" while what was right-clicked waits, else "Unacknowledge". With hints,
 * it carries the key that does the same, and so does the task entry the task key opens: "Edit task…" when there is a
 * task to move, else "Make a task…".
 *
 * @param acknowledgement - What the acknowledgement entry acts on, or null where nothing can be acknowledged.
 * @param tasks - The task entries, as `taskMenuActionsFor` lists them; empty while tasks are off.
 * @param hints - The keys to show, or null where no key acts on what was right-clicked.
 */
export function dispatchMenuEntries(
  acknowledgement: MenuAcknowledgement | null,
  tasks: readonly TaskMenuAction[],
  hints: DispatchMenuHints | null,
): DispatchMenuEntry[] {
  const entries: DispatchMenuEntry[] = [];
  if (acknowledgement) {
    const action: AcknowledgementMenuAction = { kind: acknowledgement.acknowledged ? "unacknowledge" : "acknowledge" };
    const hint = acknowledgement.acknowledged ? hints?.unacknowledge : hints?.acknowledge;
    entries.push({ action, label: acknowledgementLabel(acknowledgement), hint: hint ?? null });
  }
  // The task key moves the task already on the target, and makes one where there is none.
  const keyed = tasks.find((task) => task.kind === "move") ?? tasks.find((task) => task.kind === "make");
  for (const task of tasks) {
    entries.push({ action: task, label: taskMenuLabel(task), hint: task === keyed ? (hints?.task ?? null) : null });
  }
  return entries;
}

/** What a right-click on a table row offers, and the rows its entries act on. */
export type RowMenu = {
  /** The rows the entries act on: every ticked row when the right-clicked one is ticked, else that row alone. */
  rows: readonly DispatchTableRow[];
  /** Whether the entries act on the ticked rows, which then clear as they do after the keys. */
  viaTicks: boolean;
  entries: DispatchMenuEntry[];
};

/**
 * The entries a right-click on a table row offers, and the rows they act on.
 *
 * A right-click on a ticked row acts on every ticked row, as the keys do while rows are ticked; on any other row it acts
 * on that row alone and leaves the ticks be. The acknowledgement entry handles the rows as A does, or reads
 * "Unacknowledge" when none of them waits, as Shift+A takes them back; a row with no channel and no task to close has
 * none. The task entries are a lone row's own, or "Make a task…" for several rows. The keys' hints show only where
 * the keys would act on the same rows: not on an unticked row while others are ticked.
 *
 * @param row - The row right-clicked.
 * @param ticked - The ticked rows; empty when none is ticked.
 * @param grain - The view's grain, which decides whether a row stands for its message or its driver.
 * @param fold - The workspace's open tasks, or null while tasks are off.
 * @param shelfTasksByRow - The tasks each shelf row stands for, by row id; none off the shelf.
 * @param hints - The keys the page binds, as their keycaps read.
 */
export function rowMenuFor(
  row: DispatchTableRow,
  ticked: readonly DispatchTableRow[],
  grain: DispatchGrain,
  fold: TaskFold | null,
  shelfTasksByRow: ReadonlyMap<string, readonly OpenTask[]>,
  hints: DispatchMenuHints,
): RowMenu {
  const viaTicks = ticked.some((candidate) => candidate.id === row.id);
  const rows = viaTicks ? ticked : [row];
  // A shelf row closes its tasks with no channel; any other row acknowledges on its channel.
  const acknowledgeable = rows.some((candidate) => candidate.kind === "task" || rowFeedId(candidate) !== null);
  const acknowledgement = acknowledgeable
    ? { noun: "item" as const, acknowledged: !rows.some(rowUnacknowledged) }
    : null;
  let tasks: readonly TaskMenuAction[] = [];
  if (fold !== null)
    tasks = rows.length === 1 ? rowTaskMenuActions(row, grain, fold, shelfTasksByRow) : [{ kind: "make" }];
  const keysActHere = viaTicks || ticked.length === 0;
  return { rows, viaTicks, entries: dispatchMenuEntries(acknowledgement, tasks, keysActHere ? hints : null) };
}
